/*
<summary>
   This functional component is all about rendering the Tool Management list/grid,
   its filters, pagination, import/export actions and the loan-out/return/validate flows.
   Developer: Aashish Singh, Created Date: 20-Aug-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import { PersonFill } from 'react-bootstrap-icons';
import { useHistory } from 'react-router-dom';
import RoutesConstants from '../../shared-components/routes/routes-constants';
import noMessageIcon from '../../assets/Images/filterNodata.png';
import { emptyValue } from '../../constants/constants';
import { ModuleType } from '../../constants/enums/module-type-enum';
import CalibrationStatusEnum from '../../constants/enums/calibration-status-enum';
import InspectionCycleEnum from '../../constants/enums/inspection-cycle-enum';
import InspectionFormatEnum from '../../constants/enums/inspection-format-enum';
import InspectionStatusEnum from '../../constants/enums/inspection-status-enum';
import LoanRecordEnum from '../../constants/enums/loan-record-enum';
import MountingStatusEnum from '../../constants/enums/mounting-status-enum';
import ToolTypeEnum from '../../constants/enums/tool-type-enum';
import UsageStatusEnum from '../../constants/enums/usage-status-enum';
import {
  ToolCalibrationStatusOptions,
  ToolInspectionFormatOptions,
  ToolInspectionStatusOptions,
  ToolLoanRecordOptions,
  ToolMountingStatusOptions,
  ToolUsageStatusOptions,
} from './option';
import userSettingEnum from '../../constants/enums/user-setting-enum';
import userTypeEnum from '../../constants/enums/user-type-enum';
import { GridRemoteConstant } from '../../constants/grid-constant';
import { downloadCsvFromBase64 } from '../../core/stores/utils/download-csv';
import { formatGridDate, formatGridDateTime } from '../../helpers/date-time-helper';
import { useStore } from '../../contexts/store-provider';
import authStore from '../../core/stores/auth-store';
import {
  ToolManagementDefaultSortColumnOrder,
  ToolManagementFilterInitialState,
} from '../../core/initialState/filter-initial-state';
import {
  isOperationPermittedRoleBase,
  showIconsRoleBase,
} from '../../helpers/action-permission-helper';
import {
  getFactoryIdFromLocalStorage,
  getTenantAccessFromLocalStorage,
  getTenantIdFromLocalStorage,
  getToolManagementFilterFromLocalStorage,
  getToolManagementSelectedToolType,
  getUserId,
  setToolManagementFilterToLocalStorage,
  setToolManagementSelectedToolType,
} from '../../helpers/localstorage-helper';
import IPageInfo, {
  IActionFormatter,
  IOption,
  IPageLimitInfo,
} from '../../models/ICommon';
import IDefaultColumSortOrder from '../../models/IDefaultColumSortOrder';
import { IToolManagementFilter } from '../../models/IToolManagementFilter';
import {
  IToolManagementList,
  IToolTrayUsageInfo,
} from '../../models/response/IToolManagementResponse';
import ApplicatorSettingPopup from './components/applicator-setting-popup';
import FormLoader from '../../shared-components/FormLoader/FormLoader';
import Grid from '../../shared-components/Grid/Grid';
import { IconAndMoreActionFormatter } from '../../shared-components/Grid/GridFormatter';
import Pagination from '../../shared-components/Grid/Pagination';
import FilterPopupHOC from '../../shared-components/hoc/filter-popup-hoc/filter-popup-hoc';
import DeletePopup from '../../shared-components/popup/delete/delete-popup';
import DeletePopupHOC from '../../shared-components/popup/delete/delete-popup-hoc';
import {
  DeleteIcon,
  EditIcon,
  ExportHolidayIcon,
  ImportHolidayIcon,
  PerformInspectionIcon,
  PlusIcon,
  RefreshIcon,
  ReturnToolIcon,
  ScanIcon,
  TagReassignIcon,
  VerifiedIcon,
  ApplicatorToolIcon,
  GeneralToolIcon,
  MainUnitIcon,
} from '../../shared-components/svgs/svgs';
import { formatMessage } from '../../translations/format-message';
import AddEditToolManagement from './components/add-edit-tool-management';
import BorrowTool from './components/borrow-tool';
import ImportToolManagement from './components/import-tool-management';
import LoanOutTool from './components/loan-out-tool';
import ReturnTool from './components/return-tool';
import ValidateToolPopup from './components/validate-tool-popup';
import FilterComponent from './components/filter';
import { resolveProcessUsedDisplayNames } from './process-used-helper';
import AddEditToolManagementPopupHOC from './hoc/add-edit-tool-management-popup-hoc';
import BorrowToolPopupHOC from './hoc/borrow-tool-popup-hoc';
import ImportToolManagementPopupHOC from './hoc/import-tool-management-popup-hoc';
import LoanOutToolPopupHOC from './hoc/loan-out-tool-popup-hoc';
import ReturnToolPopupHOC from './hoc/return-tool-popup-hoc';
import './tool-management.css';
import { Button, Overlay, OverlayTrigger, Popover, Tooltip } from 'react-bootstrap';
import { ExclamationCircleFill, ListUl } from 'react-bootstrap-icons';

/**
* This function is used to determine whether any filterable field (excluding Column/Order,
* which are sort state, not a filter) is set to a non-default value. 
* @param filter: Filter Object
*/
const hasActiveFilter = (filter: IToolManagementFilter): boolean =>
  (!!filter.OwnerFactoryId && filter.OwnerFactoryId !== '-1') ||
  !!filter.ToolManagementNo ||
  !!filter.ToolName ||
  !!filter.SerialNo ||
  (!!filter.InspectionStatus && filter.InspectionStatus !== '-1') ||
  (!!filter.CalibrationStatus && filter.CalibrationStatus !== '-1') ||
  (!!filter.UsageStatus && filter.UsageStatus !== '-1');


const SORT_COLUMN_TO_API_FIELD: Record<string, string> = {
  ToolManagementNo: 'tool_management_no',
  ToolName: 'tool_name',
};
const toApiSortColumn = (dataField: string): string =>
  SORT_COLUMN_TO_API_FIELD[dataField] || dataField;

/**
* This functional component renders the Tool Management grid along with its
* pagination, filter, import/export and loan-out/return/validate/discard actions.
* @param props: Popup toggle handlers passed down by the wrapping HOCs
*/
const ToolManagement = (props: any) => {
  const { toolManagementStore, preferencesStore, factoryStore, processStore } = useStore();
  const {
    GetToolManagementListService,
    allToolManagements,
    toolManagementList,
    deleteToolManagementState,
    DeleteToolManagementService,
    resetDeleteToolManagement,
    resetToolManagementDetail,
    addUpdateToolManagementState,
    resetAddUpdateToolManagementState,
    toolManagementState,
    getAllToolManagementState,
    fileUploadState,
    resetFileUploadState,
    importResults,
    exportFileState,
    ExportToolManagementFileService,
    resetExportFileState,
    base64String,
    UpdateCalibrationExpiryService,
    resetValidateToolState,
    validateToolState,
    addEditApplicatorMountingState,
    AddEditApplicatorMountingService,
    mainUnitOptions,
    mainUnitList,
    mainUnitOptionsState,
    GetAllAvailableMainUnitsService,
    unmountApplicatorState,
    UnmountApplicatorService,
    GetTrayIdsByToolManagementNoService,
    resetTrayIdsByTool,
    reset
  } = toolManagementStore;

  const { allAvailableFactories, GetAllFactoriesService } = factoryStore;
  const { GetAllProcessService } = processStore;

  const {
    userSetting,
    setUserSetting,
    language,
    islanguageChanged,
    updateLanguageChangedFlag,
  } = preferencesStore;

  const history = useHistory();
  const allToolManagementData = allToolManagements;
  const isFirstLoad = useRef(true);
  const trayIdsRequestToolIdRef = useRef<number | null>(null);
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState(0);
  const [showValidatePopup, setShowValidatePopup] = useState(false);
  const [selectedRow, setSelectedRow] = useState<IToolManagementList | null>(null);
  const [showApplicatorSettingPopup, setShowApplicatorSettingPopup] = useState(false);
  const [showTrayIdsPopup, setShowTrayIdsPopup] = useState(false);
  // The grid's cells sit inside a horizontally-scrolling .table-responsive container, which
  // clips any absolutely-positioned child that overflows it.
  const [trayIdsAnchorEl, setTrayIdsAnchorEl] = useState<HTMLElement | null>(null);
  // Tracked locally (in addition to the store) and updated straight off the service call's own
  // .then() - guarantees the popover updates the moment the response lands, regardless of
  // whatever else may or may not be re-rendering off the store's observable state.
  const [trayIdsLoading, setTrayIdsLoading] = useState(false);
  const [fetchedTrayIds, setFetchedTrayIds] = useState<IToolTrayUsageInfo[]>([]);
  // Filter values are persisted so returning to Tool Management restores the last applied
  const [filterObj, setFilterObj] = useState<IToolManagementFilter>(() => {
    const persistedFilter = getToolManagementFilterFromLocalStorage();
    if (persistedFilter) return persistedFilter;
    const accessedFactoryId = getTenantAccessFromLocalStorage();
    return accessedFactoryId !== -1
      ? { ...ToolManagementFilterInitialState, OwnerFactoryId: String(accessedFactoryId) }
      : ToolManagementFilterInitialState;
  });
  const [isFilterCleared, setIsFilterCleared] = useState(!hasActiveFilter(filterObj));
  const [columSortOrder, setColumnSortOrder] = useState<IDefaultColumSortOrder>(
    ToolManagementDefaultSortColumnOrder
  );
  // Selected Tool Type tab, persisted so returning to Tool Management keeps the last
  // selected tab.
  const [selectedToolType, setSelectedToolType] = useState<number>(
    Number(getToolManagementSelectedToolType()) || ToolTypeEnum.GeneralTool
  );
  const pageLimitOptions: Array<number> = [10, 20, 50, 100];

  const [pageLimitInfo, setPageLimit] = useState<IPageLimitInfo>({
    pageLimit: userSetting.pageLimitConfig,
    isPageLimitChange: false,
  });

  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });

  /**
  * This function is used to serialize a filter object into the "#"-delimited query
  * string used by the export API call.
  * @param filter: Filter Object
  */
  const createQueryString = (filter: IToolManagementFilter): string => {
    return Object.entries(filter)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('#');
  };

  /**
  * This function is used to fetch the Tool Management list for the current page/page size,
  * tool type tab and filter — ActionType is 1 when no filter is applied and 2 when it is.
  * @param filter: Filter values to send (defaults to the current filterObj state)
  * @param isFilter: Whether the request resets the grid back to page 1
  * @param toolType: Selected tab to send (defaults to the current selectedToolType state)
  */
  const getToolManagementListServiceHandler = (
    filter: IToolManagementFilter = filterObj,
    isFilter = false,
    toolType: number = selectedToolType
  ) => {
    GetToolManagementListService(
      isFilter ? 1 : pageInfo.currentPage,
      pageLimitInfo.pageLimit,
      toolType,
      filter,
      hasActiveFilter(filter) ? 2 : 1
    );
  };

  /**
  * This function is used to switch the selected Tool Type tab, persist it to local storage
  * and reload the list for the newly selected tab.
  * @param toolType: ToolTypeEnum value to switch to
  */
  const handleToolTypeChange = (toolType: number) => {
    if (toolType === selectedToolType) return;
    // The Grid below is keyed on selectedToolType, so switching tabs remounts it — in remote
    // mode react-bootstrap-table-next fires its own phantom onTableChange('sort', ...) as part
    // of that remount, which would otherwise trigger a second, duplicate list fetch on top of
    // the explicit one below. isFirstLoad normally only swallows that phantom call on the
    // component's true first mount, so it has to be re-armed here for every later remount too.
    isFirstLoad.current = true;
    setSelectedToolType(toolType);
    setToolManagementSelectedToolType(toolType);
    setPageInfo({ ...pageInfo, currentPage: 1, isPagerChange: false });
    getToolManagementListServiceHandler(filterObj, true, toolType);
  };

  useEffect(() => {
    // Owner Factory (filter bar, Add/Edit form) and Borrower (Lend popup) are cross-factory
    // pickers — they must always list every factory, not just the logged-in user's own one.
    GetAllFactoriesService(true);
    GetAllProcessService();
    getToolManagementListServiceHandler();
    reset()
  }, []);

  useEffect(() => {
    if (islanguageChanged) {
      getToolManagementListServiceHandler();
      updateLanguageChangedFlag();
    }
  }, [islanguageChanged]);

  useEffect(() => {
    setPageInfo({
      ...pageInfo,
      currentPage: toolManagementList?.PagingDetails?.CurrentPage,
      totalPages: toolManagementList?.PagingDetails?.TotalPages,
      isPagerChange: false,
    });
    setDisablePagination(false);
  }, [toolManagementList]);

  useEffect(() => {
    if (pageInfo.isPagerChange) {
      getToolManagementListServiceHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.currentPage]);

  useEffect(() => {
    if (pageLimitInfo.isPageLimitChange) {
      getToolManagementListServiceHandler();
      setPageLimit({ ...pageLimitInfo, isPageLimitChange: false });
    }
  }, [pageLimitInfo.pageLimit]);

  useEffect(() => {
    if (fileUploadState.success) {
      toast.success(formatMessage('ImportSuccess'));
      resetFileUploadState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploadState.success]);

  useEffect(() => {
    // Driven off importResults itself (not fileUploadState.success/.error) because the store
    // assigns it a brand-new array on every import response — success, partial success, or
    // total failure alike — so this fires reliably exactly once per response regardless of
    // which of the sibling success/error effects (here or in ImportToolManagement) happens to
    // run first, and regardless of whether two responses in a row carry the same Message
    // string (which wouldn't re-trigger an effect keyed off fileUploadState.error/.success).
    // A partial-success import still inserted its successful rows, so the grid needs
    // refreshing there too, not just on full success.
    if (importResults.length > 0 && importResults.some((result) => result.Success)) {
      getToolManagementListServiceHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importResults]);

  useEffect(() => {
    if (addUpdateToolManagementState.success) {
      resetAddUpdateToolManagementState();
      getToolManagementListServiceHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addUpdateToolManagementState.success]);

  useEffect(() => {
    if (deleteToolManagementState.success) {
      toast.success(formatMessage('deleted_success'));
      props.deletePopupToggleHandler();
      resetDeleteToolManagement();
      getToolManagementListServiceHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteToolManagementState.success]);

  useEffect(() => {
    if (validateToolState.success) {
      toast.success(formatMessage('calibration_expiry_updated_success'));
      resetValidateToolState();
      setShowValidatePopup(false);
      setSelectedRow(null);
      getToolManagementListServiceHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateToolState.success]);

  useEffect(() => {
    if (validateToolState.error) {
      toast.error(formatMessage(validateToolState.error));
      resetValidateToolState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateToolState.error]);

  /**
  * This function is used to convert the base64 CSV string returned by the export API
  * into a downloadable file and trigger the browser download.
  * @param base64Data: Base64 Encoded CSV File Content
  */
  const handleExport = (base64Data: string) => {
    downloadCsvFromBase64(base64Data, 'ToolManagement');
  };

  useEffect(() => {
    if (exportFileState.success && base64String.fileString.length > 0) {
      handleExport(base64String.fileString);
      resetExportFileState();
    }
  }, [exportFileState.success]);

  useEffect(() => {
    if (exportFileState.error) {
      toast.error(formatMessage(exportFileState.error));
      resetExportFileState();
    }
  }, [exportFileState.error]);

  const onPageChanged = (pageNumber: number) => {
    setPageInfo({ ...pageInfo, currentPage: pageNumber, isPagerChange: true });
  };

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const onPageLimitChanged = (evt: any) => {
    setPageInfo({ ...pageInfo, currentPage: 1, isPagerChange: false });
    setPageLimit({
      pageLimit: parseInt(evt.target.value),
      isPageLimitChange: true,
    });
    setUserSetting(userSettingEnum.UserLimitConfig, parseInt(evt.target.value));
  };

  const refreshClickHandler = () => {
    getToolManagementListServiceHandler();
  };

  const deleteHandler = () => {
    DeleteToolManagementService(
      selectedId,
      selectedRow?.ToolManagementNo || '',
      Number(getTenantIdFromLocalStorage())
    );
  };

  const deleteClickHandler = (
    row: any,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    setSelectedId(row.ToolId);
    setSelectedRow(row);
    event.stopPropagation();
    resetToolManagementDetail();
    props.deletePopupToggleHandler();
  };

  const openAddToolHandler = (
    row: { ToolId: number },
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    resetToolManagementDetail();
    setSelectedId(row.ToolId);
    props.addToggleHandler();
  };

  const openImportFileHandler = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    props.importToggleHandler();
  };

  /**
  * This function is used to navigate to the Crimp Tool / User Created Inspection page for
  * the given row, based on its InspectionFormat.
  * @param row: Tool Management List Row
  */
  const performInspectionClickHandler = (
    row: IToolManagementList,
    event?: React.MouseEvent<HTMLButtonElement | HTMLElement, MouseEvent>
  ) => {
    event?.stopPropagation();
    if (!row.CanPerformInspection) return;
    if (row.UsageStatus === UsageStatusEnum.InUse) {
      toast.error(formatMessage('ToolInUse'));
      return;
    }
    const routePath =
      row.InspectionFormat === InspectionFormatEnum.CrimpingToolInspection
        ? RoutesConstants.CrimpToolInspection
        : RoutesConstants.UserCreatedInspection;
    history.push(routePath.replace(':toolId', String(row.ToolId)));
  };

  const loanOutClickHandler = (
    row: IToolManagementList,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    if (row.UsageStatus === UsageStatusEnum.InUse) {
      toast.error(formatMessage('ToolInUse'));
      return;
    }
    setSelectedRow(row);
    props.loanOutToggleHandler();
  };

  const returnClickHandler = (
    row: IToolManagementList,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    if (row.UsageStatus === UsageStatusEnum.InUse) {
      toast.error(formatMessage('ToolInUse'));
      return;
    }
    setSelectedRow(row);
    props.returnToggleHandler();
  };

  const borrowClickHandler = (
    row: IToolManagementList,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    setSelectedRow(row);
    props.borrowToggleHandler();
  };

  const validateClickHandler = (
    row: IToolManagementList,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    setSelectedRow(row);
    setShowValidatePopup(true);
  };

  /**
  * This function is used to fetch and show the tray ids a tool has been used/scanned
  * against, via GetTrayIdsByToolManagementNo.
  * @param row: Tool Management List Row
  * @param event: Click Event, stopped so it doesn't also trigger the row click
  */
  const showTrayIdsClickHandler = (
    row: IToolManagementList,
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    // Toggle off if the same row's icon is clicked again while its popover is already open.
    if (showTrayIdsPopup && selectedRow?.ToolId === row.ToolId) {
      closeTrayIdsPopover();
      return;
    }
    setSelectedRow(row);
    setTrayIdsAnchorEl(event.currentTarget);
    setShowTrayIdsPopup(true);
    setTrayIdsLoading(true);
    setFetchedTrayIds([]);
    trayIdsRequestToolIdRef.current = row.ToolId;
    GetTrayIdsByToolManagementNoService(
      row.ToolManagementNo,
      Number(getTenantIdFromLocalStorage())
    ).then(() => {
      if (trayIdsRequestToolIdRef.current !== row.ToolId) return;
      setFetchedTrayIds([...toolManagementStore.trayIdsByTool]);
      setTrayIdsLoading(false);
    });
  };

  const closeTrayIdsPopover = () => {
    setShowTrayIdsPopup(false);
    setTrayIdsAnchorEl(null);
    setTrayIdsLoading(false);
    setFetchedTrayIds([]);
    resetTrayIdsByTool();
  };

  useEffect(() => {
    if (!showTrayIdsPopup) return;

    const handleScroll = () => closeTrayIdsPopover();
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTrayIdsPopup]);

  useEffect(() => {
    if (!showTrayIdsPopup) return;

    const handleOutsideMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (trayIdsAnchorEl?.contains(target)) return;
      const popoverEl = document.getElementById('tray-ids-popover');
      if (popoverEl?.contains(target)) return;
      closeTrayIdsPopover();
    };
    document.addEventListener('mousedown', handleOutsideMouseDown, true);
    return () => document.removeEventListener('mousedown', handleOutsideMouseDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTrayIdsPopup]);

  /**
  * This function is used to submit the updated calibration expiry date for the selected row.
  * @param calibrationExpiryDate: New Calibration Expiry Date
  */
  const validateSubmitHandler = (
    calibrationExpiryDate: string,
    calibrationStatus: number | null
  ) => {
    if (!selectedRow) return;
    UpdateCalibrationExpiryService({
      ToolId: selectedRow.ToolId,
      ToolNumber: selectedRow.ToolManagementNo,
      CalibrationExpiryDate: calibrationExpiryDate,
      CalibrationStatus: calibrationStatus,
      UpdatedBy: Number(getUserId()) || 1,
      TenantId: Number(getTenantIdFromLocalStorage()),
    });
  };

  /**
  * This function is used to open the Applicator Setting popup for an Applicator row's "Mounted
  * Main Unit" column, loading the available Main Unit dropdown options.
  * @param row: Tool Management List Row
  * @param event: Click Event, stopped so it doesn't also trigger the row click
  */
  const mainUnitSettingClickHandler = (
    row: IToolManagementList,
    event?: React.MouseEvent<HTMLElement, MouseEvent>
  ) => {
    event?.stopPropagation();
    if (row.ToolType !== ToolTypeEnum.Applicator) return;
    if (!row.CanMountApplicator) return;
    setSelectedRow(row);
    const mainUnitOwnerFactoryId =
      row.LoanRecord === LoanRecordEnum.Yes && row.LoanFactoryId
        ? Number(row.LoanFactoryId)
        : Number(row.OwnerFactoryId);
    GetAllAvailableMainUnitsService(mainUnitOwnerFactoryId);
    setShowApplicatorSettingPopup(true);
  };

  /**
  * This function is used to mount/swap the selected Applicator row onto the given Main Unit,
  * then navigate to the Applicator's Crimp Tool / User Created Inspection page, 
  * unless the Applicator's InspectionCycle is None, since a None-cycle tool has no
  * inspection to perform.
  * @param mainUnitToolId: The Main Unit's tool id picked from the popup's dropdown
  */
  const applicatorSettingSubmitHandler = (mainUnitToolId: number) => {
    if (!selectedRow) return;
    const mainUnit = mainUnitList.find((m) => m.ToolId === mainUnitToolId);
    const applicatorToolId = selectedRow.ToolId;
    const isNoneInspectionCycle = selectedRow.InspectionCycle === InspectionCycleEnum.None;
    const inspectionRoutePath =
      selectedRow.InspectionFormat === InspectionFormatEnum.CrimpingToolInspection
        ? RoutesConstants.CrimpToolInspection
        : RoutesConstants.UserCreatedInspection;
    AddEditApplicatorMountingService({
      ToolId: mainUnitToolId,
      ToolName: mainUnit?.ToolName || '',
      ApplicatorId: applicatorToolId,
      ApplicatorName: selectedRow.ToolName,
      OldMainUnitId: selectedRow.MountedMainunitId,
      UpdatedBy: Number(getUserId()) || 1,
      TenantId: Number(getTenantIdFromLocalStorage()),
      MountingTime: new Date().toISOString(),
    }).then((isSuccess: boolean) => {
      if (!isSuccess) return;
      toast.success(formatMessage('applicator_mounted_success'));
      setShowApplicatorSettingPopup(false);
      if (isNoneInspectionCycle) {
        getToolManagementListServiceHandler();
        return;
      }
      history.push(inspectionRoutePath.replace(':toolId', String(applicatorToolId)));
    });
  };

  /**
  * This function is used to unmount the selected Applicator row from whichever Main Unit it's
  * currently mounted on. Only reachable from the "Mounted Main Unit" column's flow.
  */
  const applicatorSettingUnmountHandler = () => {
    if (!selectedRow) return;
    UnmountApplicatorService({
      ApplicatorId: selectedRow.ToolId,
      ApplicatorName: selectedRow.ToolName,
      ToolName: selectedRow.MountedMainunitName || '',
      UpdatedBy: Number(getUserId()) || 1,
      TenantId: Number(getTenantIdFromLocalStorage()),
    }).then((isSuccess: boolean) => {
      if (!isSuccess) return;
      setShowApplicatorSettingPopup(false);
      getToolManagementListServiceHandler();
    });
  };

  const currentFactoryId = Number(getFactoryIdFromLocalStorage());
  const isOperatorRole = authStore.getUserType === userTypeEnum[userTypeEnum.Operator];

  /**
  * This function is used to check whether the currently accessed factory is eligible to act on the given tool
  * row (Owner Factory, or Loaned-Out Factory once on loan).
  * @param row: Tool Management List Row
  */
  const isFactoryEligible = (row: IToolManagementList) => {
    // LoanFactoryId is only set for a factory-to-factory loan — a subcontractor loan has no
    // borrowing factory, so falling back to OwnerFactoryId here keeps the owning factory able
    // to manage its own tool while it's out with a subcontractor, instead of locking everyone
    // out because LoanFactoryId is null.
    if (row.LoanRecord === LoanRecordEnum.Yes && row.LoanFactoryId) {
      return Number(row.LoanFactoryId) === currentFactoryId;
    }
    return Number(row.OwnerFactoryId) === currentFactoryId;
  };

  const isOwnerFactory = (row: IToolManagementList) =>
    Number(row.OwnerFactoryId) === currentFactoryId;

  // A tool whose calibration is Expired, or whose calibration / use-by date has already passed,
  // may only be Validated (which sets a new expiry date) - every other row action is blocked.
  const isCalibrationLapsed = (row: IToolManagementList) => {
    if (row.CalibrationStatus === CalibrationStatusEnum.Expired) return true;
    if (!row.CalibrationExpiryDate) return false;
    const expiry = new Date(row.CalibrationExpiryDate);
    if (isNaN(expiry.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    return expiry < today;
  };

  // A tool whose disposal date has arrived (today or earlier) is disposed of - no row action available
  const isDisposed = (row: IToolManagementList) => {
    if (!row.DisposalDate) return false;
    const disposal = new Date(row.DisposalDate);
    if (isNaN(disposal.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    disposal.setHours(0, 0, 0, 0);
    return disposal <= today;
  };

  // The API only sends OwnerFactoryId/LoanFactoryId as ids, so the grid maps them to
  // display names off the same allAvailableFactories list Step 1's factory select uses.
  // ProcessUsed is already the process name (a string), so it needs no lookup.
  /**
  * This function is used to resolve a factory id to its display name for the grid.
  * @param factoryId: Factory Identifier
  */
  const getFactoryName = (factoryId: number | null | undefined) =>
    allAvailableFactories.find((option) => option.id === Number(factoryId))?.value || '-';

  /**
  * This function is used to enrich the raw tool rows with display names and the
  * per-row action permission flags (edit/discard/inspection/loan-out/return/validate)
  * consumed by the grid's action column.
  * @param rows: Tool Management List Rows
  */
  const withRowPermissions = (rows: IToolManagementList[]): IToolManagementList[] =>
    rows.map((row) => {
      const factoryEligible = isFactoryEligible(row);
      // Expired / past-date tools: only Validate stays available.
      const disposed = isDisposed(row);
      const isUnusable = row.UsageStatus === UsageStatusEnum.Unusable;
      const actionsAllowed =
        factoryEligible && !disposed && !isCalibrationLapsed(row) && !isUnusable;
      return {
        ...row,
        OwnerFactoryName: getFactoryName(row.OwnerFactoryId),
        LoanFactoryName: row.LoanFactoryId ? getFactoryName(row.LoanFactoryId) : '-',
        ProcessUsedName: resolveProcessUsedDisplayNames(row.ProcessUsed),
        CanEdit:
          isOwnerFactory(row) &&
          !disposed &&
          !isCalibrationLapsed(row) &&
          !isOperatorRole &&
          isOperationPermittedRoleBase(ModuleType.ToolManagement, 'Edit'),
        CanDiscard:
          isOwnerFactory(row) &&
          !disposed &&
          row.LoanRecord !== LoanRecordEnum.Yes &&
          !isOperatorRole &&
          isOperationPermittedRoleBase(ModuleType.ToolManagement, 'Delete'),
        // HasInspectionInfo === false means the tool has no inspection info configured (the
        // grid already flags this with a "!" next to the tool name), so there's nothing to
        // perform an inspection against yet. A tool whose Inspection Cycle is "None" is never
        // inspected at all, so Perform Inspection is disabled for it too.
        CanPerformInspection:
          actionsAllowed &&
          row.HasInspectionInfo !== false &&
          row.InspectionCycle !== InspectionCycleEnum.None,
        // A tool currently In Use can't actually be loaned out or returned, but the button
        // stays enabled - clicking it while InUse shows a toast error instead (see
        // loanOutClickHandler / returnClickHandler) rather than disabling/hiding the button.
        CanLoanOut: actionsAllowed && row.LoanRecord !== LoanRecordEnum.Yes,
        // Return is only actionable once the tool actually has a loan-out
        // date recorded, not merely a "Yes" flag.
        CanReturn: actionsAllowed && row.LoanRecord === LoanRecordEnum.Yes && !!row.LoanDate,
        // Borrower assigns/reassigns the physical tag while the tool is on loan, so it's
        // enabled under the same condition as Return.
        CanBorrow: actionsAllowed && row.LoanRecord === LoanRecordEnum.Yes && !!row.LoanDate,
        CanValidate: factoryEligible && !disposed && !isUnusable,
        // Applicator tab's "Mounted Main Unit" cell button — same factory eligibility as the
        // other row actions (owner factory when not on loan, borrower factory when loaned out).
        CanMountApplicator: actionsAllowed,
        // Tray Ids icon (Usage Status column) - only for tools currently In Use, and only for
        // the tool's own factory or the factory it's currently loaned out to.
        CanViewTrayIds: factoryEligible && row.UsageStatus === UsageStatusEnum.InUse,
      };
    });

  /**
  * This function is used to trigger the CSV export of the currently filtered/sorted list.
  */
  const exportClickHandler = () => {
    const filter = createQueryString({
      ...filterObj,
      Column: toApiSortColumn(columSortOrder.dataField),
      Order: columSortOrder.order,
    });
    ExportToolManagementFileService(
      Number(getTenantIdFromLocalStorage()),
      encodeURIComponent(filter), language);
  };

  /**
  * This function is used to apply the filter bar values and refresh the grid from page 1.
  * @param object: Filter Values
  */
  const filterHandler = (object: IToolManagementFilter) => {
    const filters = {
      ...object,
      Column: toApiSortColumn(columSortOrder.dataField),
      Order: columSortOrder.order,
    };
    getToolManagementListServiceHandler(filters, true);
    setFilterObj({ ...filters });
    setToolManagementFilterToLocalStorage(filters);
    setIsFilterCleared(!hasActiveFilter(filters));
  };

  /**
  * This function is used to sync the filter bar state, and to refresh the grid whenever
  * the filters were cleared.
  * @param obj: Filter Values
  * @param isfilterCleard: Whether the filter was cleared
  */
  const filterParams = (obj: IToolManagementFilter, isfilterCleard: boolean) => {
    if (isfilterCleard) {
      setFilterObj({ ...obj });
      setToolManagementFilterToLocalStorage(obj);
      getToolManagementListServiceHandler(obj, true);
      setIsFilterCleared(true);
    } else {
      setFilterObj({ ...obj });
    }
  };

  /**
  * This function is used to react to grid sort changes and refresh the list accordingly.
  * @param type: Table Change Type
  * @param newState: New Sort/Table State
  */
  const handleTableChange = (type: any, newState: any) => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    setColumnSortOrder((prevState) => ({
      ...prevState,
      dataField: newState.sortField,
      order: newState.sortOrder,
    }));
    const filter = {
      ...filterObj,
      Column: toApiSortColumn(newState.sortField),
      Order: newState.sortOrder,
    };
    setFilterObj(filter);
    getToolManagementListServiceHandler(filter, true);
  };

  const actionIcons: IActionFormatter[] = showIconsRoleBase(
    [
      {
        dataField: 'CanEdit',
        handler: openAddToolHandler,
        icon: <EditIcon />,
        isDisable: true,
        title: 'edit',
        varient: 'primary',
        action: 'edit',
      },
      {
        dataField: 'CanPerformInspection',
        handler: performInspectionClickHandler,
        icon: <PerformInspectionIcon />,
        isDisable: true,
        title: 'perform_inspection',
        varient: 'success',
        action: 'perform_inspection',
      },
      {
        dataField: 'CanLoanOut',
        handler: loanOutClickHandler,
        icon: <TagReassignIcon />,
        isDisable: true,
        title: 'loan_out',
        varient: 'secondary',
        action: 'loan_out',
      },
      {
        dataField: 'CanReturn',
        handler: returnClickHandler,
        icon: <ReturnToolIcon />,
        isDisable: true,
        title: 'return_tool',
        varient: 'secondary',
        action: 'return_tool',
      },
      {
        dataField: 'CanBorrow',
        handler: borrowClickHandler,
        icon: <ScanIcon />,
        isDisable: true,
        title: 'borrow_tool',
        varient: 'secondary',
        action: 'borrow_tool',
      },
      {
        dataField: 'CanValidate',
        handler: validateClickHandler,
        icon: <VerifiedIcon />,
        isDisable: true,
        title: 'validation',
        varient: 'primary',
        action: 'validation',
      },
      {
        dataField: 'CanDiscard',
        handler: deleteClickHandler,
        icon: <DeleteIcon />,
        isDisable: true,
        title: 'delete',
        varient: 'danger',
        action: 'delete',
      },
    ],
    ModuleType.ToolManagement
  );

  /**
  * This function is used to tint a row's background by its Calibration Status — Warning rows
  * get a light amber tint, Expired rows a light red tint, matching the reference mockup.
  * @param row: Tool Management List Row
  */
  const getRowClasses = (row: IToolManagementList): string => {
    if (row.CalibrationStatus === CalibrationStatusEnum.Warning) return 'tm-row-cal-warning';
    if (row.CalibrationStatus === CalibrationStatusEnum.Expired) return 'tm-row-cal-expired';
    return '';
  };

 /**
  * This function is used as a column's `title` — it shows the full cell value as a native
  * tooltip only 
  * @param cell: Raw cell value
  */
  const showTitleTooltip = (cell: unknown): string | undefined => {
    if (cell === null || cell === undefined) return undefined;
    const text = String(cell);
    return text;
  };

  /**
  * This function returns a plain-text label formatter for a fixed-enum field, resolving the
  * cell's numeric id against the given static option list.
  * @param options: Static option list (id = enum value, value = translation key)
  */
  const staticLabelFormatterFor = (options: IOption[]) => (cell: number | null | undefined) => {
    if (cell === null || cell === undefined) return emptyValue;
    const option = options.find((o) => o.id === cell);
    return option ? <FormattedMessage id={option.value} /> : emptyValue;
  };

  /**
  * This function returns a colored-badge formatter for a fixed-enum field, resolving the
  * cell's numeric id against the given static option list and badge-class map.
  * @param options: Static option list (id = enum value, value = translation key)
  * @param badgeClassById: Enum value -> tm-badge-* class name
  */
  const staticBadgeFormatterFor =
    (options: IOption[], badgeClassById: Record<number, string>) =>
    (cell: number | null | undefined) => {
      if (cell === null || cell === undefined) return emptyValue;
      const option = options.find((o) => o.id === cell);
      return (
        <span
          className={`tm-badge d-inline-block fw-bold text-nowrap ${
            badgeClassById[cell] || 'tm-badge-inactive'
          }`}
        >
          {option ? <FormattedMessage id={option.value} /> : emptyValue}
        </span>
      );
    };

  const staticBadgeFormatterForIsLoaned = () =>
    (cell: boolean | null | undefined = false) => {
      if (cell === null || cell === undefined) return emptyValue;
      return (
        <span
          className={`tm-badge d-inline-block fw-bold text-nowrap ${cell ? "tm-badge-lend" : "tm-badge-inactive"}`}>
          <FormattedMessage id={cell ? 'loan_record_yes' : 'loan_record_no'} />
        </span>
      );
    };

  const truncatedHeaderFormatter = (
    column: { text: string },
    _colIndex: number,
    components: { sortElement?: React.ReactNode }
  ) => (
    <div className="tm-th-inner d-flex align-items-center gap-1">
      <span className="tm-th-title">{column.text}</span>
      {components.sortElement}
    </div>
  );

  const columns: any = [
    {
      dataField: 'serialNumber',
      text: formatMessage('serial_number'),
      classes: 'show-Elipses tm-sticky tm-sticky-0',
      headerClasses: 'tm-sticky tm-sticky-0',
      isDummyField: true,
      formatter: (_: any, __: any, rowIndex: number) => rowIndex + 1,
      sort: false,
    },
    {
      dataField: 'ToolManagementNo',
      text: formatMessage('tool_management_no'),
      headerClasses: '',
      formatter: (cell: string, row: IToolManagementList) => (
        <span className="d-inline-flex align-items-center gap-1">
          <span>{cell}</span>
          {row.HasInspectionInfo === false && row.InspectionCycle !== InspectionCycleEnum.None && (
            <ExclamationCircleFill
              className="text-danger"
              title={formatMessage('inspection_info_not_available')}
            />
          )}
        </span>
      ),
      title: showTitleTooltip,
      sort: true,
    },
    {
      dataField: 'ToolName',
      text: formatMessage('tool_name_equipment_name'),
      classes: 'show-Elipses tm-sticky tm-sticky-2',
      headerClasses: 'tm-sticky tm-sticky-2',
      sort: true,
      formatter: (cell: string, row: IToolManagementList) => (
        <span className="d-inline-flex align-items-center gap-1">
          <span>{cell}</span>
        </span>
      ),
      title: showTitleTooltip,
    },
    {
      dataField: 'OwnerFactoryName',
      text: formatMessage('owner_factory'),
      classes: 'show-Elipses',
      title: showTitleTooltip,
    },
    {
      dataField: 'ToolManufacturer',
      text: formatMessage('tool_manufacturer'),
      classes: 'show-Elipses',
      title: showTitleTooltip,
    },
    {
      dataField: 'ToolModel',
      text: formatMessage('tool_model'),
      classes: 'show-Elipses',
      title: showTitleTooltip,
    },
    {
      dataField: 'SerialNo',
      text: formatMessage('serial_no'),
      classes: 'show-Elipses',
      title: showTitleTooltip,
    },
    {
      dataField: 'UsageStatus',
      text: formatMessage('usage_status'),
      classes: 'show-Elipses',
      formatter: (cell: number | null, row: IToolManagementList) => {
        const badge = staticBadgeFormatterFor(ToolUsageStatusOptions, {
            [UsageStatusEnum.InUse]: 'tm-badge-inuse',
            [UsageStatusEnum.OnLoan]: 'tm-badge-lend',
            [UsageStatusEnum.Unused]: 'tm-badge-inactive',
            [UsageStatusEnum.Unavailable]: 'tm-badge-inactive',
          })(cell);

        return (
          <span className="d-inline-flex align-items-center gap-1">
            {badge}
          {row.CanViewTrayIds && (
            <button
              type="button"
              className="btn btn-light-secondary btn-sm p-1"
              title={formatMessage('show_tray_ids')}
              onClick={(event) => showTrayIdsClickHandler(row, event)}
            >
              <ListUl size={14} />
            </button>
          )}
        </span>
      );
    },
    },
    {
      dataField: 'InspectionStatus',
      text: formatMessage('inspection_status'),
      classes: 'show-Elipses',
      formatter: staticBadgeFormatterFor(ToolInspectionStatusOptions, {
        [InspectionStatusEnum.Inspected]: 'tm-badge-ok',
        [InspectionStatusEnum.NotInspected]: 'tm-badge-warn',
      }),
    },
    {
      dataField: 'LoanRecord',
      text: formatMessage('loan_status'),
      classes: 'show-Elipses',
      formatter: (cell: number | null, row: IToolManagementList) => {
        const badge = staticBadgeFormatterFor(ToolLoanRecordOptions, {
          [LoanRecordEnum.No]: 'tm-badge-inactive',
          [LoanRecordEnum.Yes]: 'tm-badge-lend',
        })(cell);

        const showLoanFactoryName =
          cell === LoanRecordEnum.Yes && row.LoanFactoryName && row.LoanFactoryName !== '-';

        return showLoanFactoryName ? (
          <OverlayTrigger placement="top" overlay={<Tooltip>{row.LoanFactoryName}</Tooltip>}>
            <span>{badge}</span>
          </OverlayTrigger>
        ) : (
          badge
        );
      },
    },
    {
      dataField: 'IsLoaned',
      text: formatMessage('loan_record'),
      classes: 'show-Elipses',
      formatter: staticBadgeFormatterForIsLoaned(),
    },
    {
      dataField: 'LoanDate',
      text: formatMessage('loan_date'),
      classes: 'show-Elipses',
      formatter: formatGridDate,
      title: showTitleTooltip,
    },
    {
      dataField: 'PlannedReturnDate',
      text: formatMessage('planned_return_date'),
      classes: 'show-Elipses',
      formatter: formatGridDate,
      title: showTitleTooltip,
    },
    {
      dataField: 'CalibrationExpiryDate',
      text: formatMessage('calibration_usage_expiry'),
      classes: 'show-Elipses',
      formatter: formatGridDate,
      title: showTitleTooltip,
    },
    {
      dataField: 'CalibrationStatus',
      text: formatMessage('calibration_status'),
      classes: 'show-Elipses',
      formatter: staticBadgeFormatterFor(ToolCalibrationStatusOptions, {
        [CalibrationStatusEnum.Valid]: 'tm-badge-ok',
        [CalibrationStatusEnum.Warning]: 'tm-badge-warn',
        [CalibrationStatusEnum.Expired]: 'tm-badge-expired',
      }),
    },
    {
      dataField: 'EquipmentCategory',
      text: formatMessage('equipment_category'),
      classes: 'show-Elipses',
      formatter: (cell: string) => cell || '-',
      title: showTitleTooltip,
    },
    {
      dataField: 'ProcessUsedName',
      text: formatMessage('process_used'),
      classes: 'show-Elipses',
      title: showTitleTooltip,
    },
    {
      dataField: 'InspectionInterval',
      text: formatMessage('inspection_interval'),
      classes: 'show-Elipses',
      formatter: (cell: number | null) => (cell !== null && cell !== undefined ? cell : '-'),
    },
    {
      dataField: 'InspectionFormat',
      text: formatMessage('inspection_format'),
      classes: 'show-Elipses',
      formatter: staticLabelFormatterFor(ToolInspectionFormatOptions),
    },
    // Mounting Status/Date/Mounted-tool columns only make sense for Main Unit and Applicator
    // rows (a General Tool is never mounted onto anything), so they're hidden on that tab.
    {
      dataField: 'MountingStatus',
      text: formatMessage('mounting_status'),
      classes: 'show-Elipses',
      hidden: selectedToolType === ToolTypeEnum.GeneralTool,
      formatter: staticBadgeFormatterFor(ToolMountingStatusOptions, {
        [MountingStatusEnum.Mounted]: 'tm-badge-ok',
        [MountingStatusEnum.NotMounted]: 'tm-badge-inactive',
      }),
    },
    {
      dataField: 'MountingTime',
      text: formatMessage('mounting_date_time'),
      classes: 'show-Elipses',
      hidden: selectedToolType === ToolTypeEnum.GeneralTool,
      formatter: formatGridDateTime,
      title: (cell: string | null) => showTitleTooltip(formatGridDateTime(cell)),
    },
    // The Applicator tab's Mounted Main Unit is clickable, opening the Applicator Setting
    // popup (Add, or Unmount/Swap once a Main Unit is already mounted). The Main Unit tab's
    // Mounted Applicator is plain, non-clickable text — no popup on this page for that column.
    // Neither is left blank/"-" when unset — showing "Unconfirmed" instead makes it clear the
    // mounting hasn't been set yet.
    selectedToolType === ToolTypeEnum.Applicator
      ? {
          dataField: 'MountedMainunitName',
          text: formatMessage('mounted_main_unit'),
          classes: 'show-Elipses',
          formatter: (cell: string | null, row: IToolManagementList) => (
            <Button
              variant='secondary'
              size='sm'
              className="w-100 tm-mounted-applicator-cell"
              onClick={(event) => mainUnitSettingClickHandler(row, event)}
              disabled={!row.CanMountApplicator}
              title={cell || formatMessage('unconfirmed')}
            >
              <span className='text-truncate d-block'>{cell || <FormattedMessage id="unconfirmed" />}</span>
            </Button>
          ),
          title: showTitleTooltip,
        }
      : {
          dataField: 'MountedApplicatorName',
          text: formatMessage('mounted_applicator'),
          classes: 'show-Elipses',
          hidden: selectedToolType === ToolTypeEnum.GeneralTool,
          formatter: (cell: string | null) => cell || <FormattedMessage id="unconfirmed" />,
          title: showTitleTooltip,
        },
    {
      dataField: 'PartsManufacturer',
      text: formatMessage('parts_manufacturer'),
      classes: 'show-Elipses',
      title: showTitleTooltip,
    },
    {
      dataField: 'ReelModel',
      text: formatMessage('reel_model'),
      classes: 'show-Elipses',
      formatter: (cell: string) => cell || '-',
      title: showTitleTooltip,
    },
    {
      dataField: 'LooseTerminalModel',
      text: formatMessage('loose_terminal_model'),
      classes: 'show-Elipses',
      formatter: (cell: string) => cell || '-',
      title: showTitleTooltip,
    },
    {
      dataField: 'ManagingDepartment',
      text: formatMessage('managing_dept'),
      classes: 'show-Elipses',
      title: showTitleTooltip,
    },
    {
      dataField: 'ResponsibleManager',
      text: formatMessage('responsible_manager'),
      classes: 'show-Elipses',
      title: showTitleTooltip,
    },
    {
      dataField: 'FixedAssetNo',
      text: formatMessage('fixed_asset_no'),
      classes: 'show-Elipses',
      title: showTitleTooltip,
    },
    {
      dataField: 'PurchaseDate',
      text: formatMessage('purchase_date'),
      classes: 'show-Elipses',
      formatter: formatGridDate,
      title: showTitleTooltip,
    },
    {
      dataField: 'DisposalDate',
      text: formatMessage('disposal_date'),
      classes: 'show-Elipses',
      formatter: formatGridDate,
      title: showTitleTooltip,
    },
    {
      dataField: 'Location',
      text: formatMessage('location'),
      classes: 'show-Elipses',
      title: showTitleTooltip,
    },
    {
      dataField: 'CurrentTagId',
      text: formatMessage('tag_id'),
      classes: 'show-Elipses',
      title: showTitleTooltip,
    },
    {
      dataField: 'Remarks',
      text: formatMessage('remarks'),
      classes: 'show-Elipses',
      formatter: (cell: string) => cell || '-',
      hidden: false,
      title: showTitleTooltip,
    },
    {
      dataField: '',
      // Edit/Delete stay as inline icon buttons (matching other grids); the remaining
      // row actions collapse into the "More" (⋮) dropdown.
      formatter: IconAndMoreActionFormatter(
        actionIcons.filter((action) => ['edit', 'delete'].includes(action.action)),
        actionIcons.filter((action) => !['edit', 'delete'].includes(action.action)),
        ['perform_inspection', 'return_tool', 'borrow_tool', 'validation']
      ),
      text: formatMessage('actions'),
      classes: 'last-column tm-actions-col tm-sticky tm-sticky-right',
      headerClasses: 'tm-actions-col tm-sticky tm-sticky-right',
      hidden: actionIcons.length > 0 ? false : true,
    },
  ].map((col: any) => ({
    ...col,
    headerFormatter: col.headerFormatter || truncatedHeaderFormatter,
  }));

  return (
    <>
      {props.showImportPopup && (
        <ImportToolManagement
          id={selectedId}
          modalClosed={props.importToggleHandler}
        />
      )}
      {props.showAddToolManagementPopup && (
        <AddEditToolManagement
          id={selectedId}
          modalClosed={props.addToggleHandler}
          selectedToolType={selectedToolType}
        />
      )}
      {props.showDeletePopup && (
        <DeletePopup
          title="delete_tool_management"
          modalSubmit={deleteHandler}
          modalClosed={props.deletePopupToggleHandler}
          message="message_delete_tool_management_confirm"
          isLoading={deleteToolManagementState.inProgress}
        />
      )}
      {props.showLoanOutToolPopup && selectedRow && (
        <LoanOutTool
          toolId={selectedRow.ToolId}
          toolManagementNo={selectedRow.ToolManagementNo}
          lenderFactoryId={Number(selectedRow.OwnerFactoryId)}
          lenderFactoryName={selectedRow.OwnerFactoryName || getFactoryName(selectedRow.OwnerFactoryId)}
          factoryOptions={allAvailableFactories}
          modalClosed={() => {
            props.loanOutToggleHandler();
            setSelectedRow(null);
          }}
          onSuccess={() => getToolManagementListServiceHandler()}
        />
      )}
      <Overlay
        show={showTrayIdsPopup}
        target={trayIdsAnchorEl}
        placement="left"
        rootClose
        onHide={closeTrayIdsPopover}
      >
        {(overlayProps) => (
          <Popover
            id="tray-ids-popover"
            {...overlayProps}
          >
            <Popover.Header className="d-flex justify-content-between align-items-center gap-2">
              <FormattedMessage id="tray_ids" />
              {fetchedTrayIds.find((tray) => tray.OperatorUserName ) && (
                <span
                  className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded-pill bg-light text-muted fw-normal show-Elipses"
                  style={{ maxWidth: '140px' }}
                  title={formatMessage('operator_name')}
                >
                  <PersonFill size={12} />
                  <span className="show-Elipses">{fetchedTrayIds.find((tray) => tray.OperatorUserName )?.OperatorUserName }</span>
                </span>
              )}
            </Popover.Header>
            <Popover.Body>
              {trayIdsLoading ? (
                <div className="text-center py-1">
                  <FormattedMessage id="loading" />
                </div>
              ) : fetchedTrayIds.length > 0 ? (
                <ul className="list-group">
                  {fetchedTrayIds.map((tray) => (
                    <li
                      key={tray.TrayId}
                      className="list-group-item py-1"
                    >
                      {tray.TrayId}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-center text-muted py-1">
                  <FormattedMessage id="no_tray_ids_found" />
                </div>
              )}
            </Popover.Body>
          </Popover>
        )}
      </Overlay>
      {showValidatePopup && selectedRow && (
        <ValidateToolPopup
          toolManagementNo={selectedRow.ToolManagementNo}
          currentExpiryDate={selectedRow.CalibrationExpiryDate}
          noOfDaysUntilWarning={selectedRow.NoOfDaysUntilWarning}
          isLoading={validateToolState.inProgress}
          modalClosed={() => {
            setShowValidatePopup(false);
            setSelectedRow(null);
          }}
          modalSubmit={validateSubmitHandler}
        />
      )}
      {props.showReturnToolPopup && selectedRow && (
        <ReturnTool
          toolId={selectedRow.ToolId}
          toolManagementNo={selectedRow.ToolManagementNo}
          borrowerFactoryId={selectedRow.LoanFactoryId}
          loanDate={selectedRow.LoanDate}
          tagId={selectedRow.TagId}
          modalClosed={() => {
            props.returnToggleHandler();
            setSelectedRow(null);
          }}
          onSuccess={() => getToolManagementListServiceHandler()}
        />
      )}
      {props.showBorrowToolPopup && selectedRow && (
        <BorrowTool
          toolId={selectedRow.ToolId}
          toolManagementNo={selectedRow.ToolManagementNo}
          toolName={selectedRow.ToolName}
          serialNo={selectedRow.SerialNo}
          tagId={selectedRow.TagId}
          currentTagId={selectedRow.CurrentTagId}
          modalClosed={() => {
            props.borrowToggleHandler();
            setSelectedRow(null);
          }}
          onSuccess={() => getToolManagementListServiceHandler()}
        />
      )}
      {showApplicatorSettingPopup && selectedRow && (
        <ApplicatorSettingPopup
          toolManagementNo={selectedRow.ToolManagementNo}
          options={mainUnitOptions}
          currentSelectedId={selectedRow.MountedMainunitId}
          currentSelectedLabel={selectedRow.MountedMainunitName}
          isLoading={
            mainUnitOptionsState.inProgress ||
            addEditApplicatorMountingState.inProgress ||
            unmountApplicatorState.inProgress
          }
          modalClosed={() => {
            setShowApplicatorSettingPopup(false);
            setSelectedRow(null);
          }}
          modalSubmit={applicatorSettingSubmitHandler}
          modalUnmount={applicatorSettingUnmountHandler}
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-xl-4 col-lg-5 col-md-6 col-sm-6">
            <div className="page_title_wrap">
              <ul className="add-list operator-back">
                <li className="process-chart">
                  <a
                    className="btn btn-secondary"
                    onClick={() => history.push(RoutesConstants.EquipmentManagement)}
                  >
                    <FormattedMessage id="back" />
                  </a>
                </li>
                <li>
                  <h1 className="page_title">
                    <FormattedMessage id="tool_management" />
                  </h1>
                </li>
              </ul>
            </div>
          </div>
          <div className="col-xl-8 col-lg-7 col-md-6 col-sm-6">
            <ul className="add-list">
              <ul className="tableAccordian-switch tm-tool-type-switch d-flex bg-white w-auto rounded-1">
                <li
                  className={`d-flex align-items-center rounded-1 ${
                    selectedToolType === ToolTypeEnum.GeneralTool ? 'active text-white' : ''
                  }`}
                  onClick={() => handleToolTypeChange(ToolTypeEnum.GeneralTool)}
                >
                  <GeneralToolIcon />
                  <span className="icon-text"><FormattedMessage id="general_tool" /></span>
                </li>
                <li
                  className={`d-flex align-items-center rounded-1 ${
                    selectedToolType === ToolTypeEnum.MainUnit ? 'active text-white' : ''
                  }`}
                  onClick={() => handleToolTypeChange(ToolTypeEnum.MainUnit)}
                >
                  <MainUnitIcon />
                  <span className="icon-text"><FormattedMessage id="main_unit" /></span>
                </li>
                <li
                  className={`d-flex align-items-center rounded-1 ${
                    selectedToolType === ToolTypeEnum.Applicator ? 'active text-white' : ''
                  }`}
                  onClick={() => handleToolTypeChange(ToolTypeEnum.Applicator)}
                >
                  <ApplicatorToolIcon />
                  <span className="icon-text"><FormattedMessage id="applicator" /></span>
                </li>
              </ul>
              <li title={formatMessage('refresh')}>
                <a
                  className="btn btn-light"
                  onClick={refreshClickHandler}
                >
                  <RefreshIcon />
                </a>
              </li>
              {isOperationPermittedRoleBase(ModuleType.ToolManagement, 'Add') && (
                <li title={formatMessage('csv_import')}>
                  <a
                    className="btn btn-light"
                    onClick={(event: any) => openImportFileHandler(event)}
                  >
                    <ImportHolidayIcon />
                  </a>
                </li>
              )}
              <li title={formatMessage('csv_export')}>
                <a
                  className="btn btn-light"
                  onClick={exportClickHandler}
                >
                  <ExportHolidayIcon />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="card card-page  uci-card">
        {(getAllToolManagementState.inProgress ||
          toolManagementState.inProgress ||
          deleteToolManagementState.inProgress ||
          addUpdateToolManagementState.inProgress ||
          fileUploadState.inProgress ||
          exportFileState.inProgress) && (
          <FormLoader
            loading={
              getAllToolManagementState.inProgress ||
              toolManagementState.inProgress ||
              deleteToolManagementState.inProgress ||
              addUpdateToolManagementState.inProgress ||
              fileUploadState.inProgress ||
              exportFileState.inProgress
            }
          />
        )}
        <FilterComponent
          initialValues={filterObj}
          submitHandler={filterHandler}
          filterParmas={filterParams}
          isLoading={false}
          isFilterCleared={isFilterCleared}
          factoryOptions={allAvailableFactories}
          showAddToolButton={isOperationPermittedRoleBase(ModuleType.ToolManagement, 'Add')}
          onAddToolClick={(event) => openAddToolHandler({ ToolId: -1 }, event)}
        />
        {allToolManagementData && allToolManagementData.length > 0 ? (
          <>
            <div className="tableDesign row-eq-height tableDevices toolMasterTable border-0 p-3 changingPanel">
              <div
                className="table-responsive tool-management-table"
                data-testid="table"
              >
                <Grid
                  key={`${language}-${selectedToolType}`}
                  data={withRowPermissions(allToolManagementData)}
                  columns={columns}
                  keyField="ToolId"
                  loading={false}
                  hover={true}
                  sort={{ ...columSortOrder }}
                  remote={{ ...GridRemoteConstant }}
                  onTableChange={handleTableChange}
                  rowClasses={getRowClasses}
                />
              </div>
            </div>
            <div className="row no-gutters p-3 pt-0 tableFooter">
              <div className="col-lg-3 col-md-6 col-sm-6 pt-1">
                <FormattedMessage id="show" />
                <select
                  className="pageLimit"
                  onChange={onPageLimitChanged}
                  value={pageLimitInfo.pageLimit}
                >
                  {pageLimitOptions.map((op: any) => (
                    <option
                      key={op}
                      value={op}
                    >
                      {op}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-lg-9 col-md-6 col-sm-6 d-flex justify-content-sm-end">
                <Pagination
                  isRefreshPage={false}
                  totalPages={pageInfo.totalPages}
                  pageLimit={pageLimitInfo.pageLimit}
                  onPageChanged={onPageChanged}
                  disablePagination={disablePagination}
                  setIsRefreshPage={setIsRefreshPage}
                  currentPage={pageInfo.currentPage}
                  totalRecords={toolManagementList.PagingDetails.TotalRecords}
                />
              </div>
            </div>
          </>
        ) : (
          !getAllToolManagementState.inProgress && (
            <div className="message">
              <div className="d-flex message-area">
                <div className="no-data-img">
                  <img
                    src={noMessageIcon}
                    alt=""
                    className="no_message"
                  />
                </div>
                <div>
                  <h3 className="fw-bold display-6">
                    <FormattedMessage id="no_tool_management" />
                  </h3>
                  <h4 className="mb-3"><FormattedMessage id="no_tool_management_message" /></h4>
                  {isOperationPermittedRoleBase(ModuleType.ToolManagement, 'Add') && (
                    <a
                      className="btn btn-primary"
                      onClick={(event: any) => openAddToolHandler({ ToolId: -1 }, event)}
                    >
                      <span className="me-1">
                        <PlusIcon />
                      </span>
                      <FormattedMessage id="add_tool_management" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          )
        )}
      </div>
    </>
  );
};

export default ImportToolManagementPopupHOC(
  FilterPopupHOC(
    AddEditToolManagementPopupHOC(
      LoanOutToolPopupHOC(
        ReturnToolPopupHOC(BorrowToolPopupHOC(DeletePopupHOC(observer(ToolManagement))))
      )
    )
  )
);
